import { compileShader, linkProgram } from './gl-program.helpers';
import type { WireframeGlResources } from './types';

/**
 * Initialize WebGL program + buffer for the wireframe overlay.
 * @param gl WebGL context provided by Mapbox custom layer
 */
export function initWireframeGl(
  gl: WebGLRenderingContext,
): WireframeGlResources {
  const vsSource = `
    precision highp float;
    attribute vec3 a_pos_high;
    attribute vec3 a_pos_low;
    attribute vec3 a_other_high;
    attribute vec3 a_other_low;
    attribute float a_side;
    attribute float a_along;
    attribute vec4 a_color;
    attribute vec2 a_data; // [distance, isDashed]
    attribute float a_width;
    uniform mat4 u_matrix;
    uniform vec2 u_resolution;
    uniform float u_line_width;
    varying vec4 v_color;
    varying float v_distance;
    varying float v_is_dashed;

    void main() {
      vec3 pos = a_pos_high + a_pos_low;
      vec3 other = a_other_high + a_other_low;

      v_color = a_color;
      v_distance = a_data.x;
      v_is_dashed = a_data.y;

      vec4 clip_pos = u_matrix * vec4(pos, 1.0);
      // Zero-width vertices are literal triangles for filled draft previews.
      if (a_width == 0.0) {
        gl_Position = clip_pos;
        return;
      }
      vec4 clip_other = u_matrix * vec4(other, 1.0);

      // Project to screen space (-1 to 1)
      vec2 screen_pos = clip_pos.xy / clip_pos.w;
      vec2 screen_other = clip_other.xy / clip_other.w;

      // Calculate direction in screen space (pixels)
      vec2 dir = normalize((screen_other - screen_pos) * u_resolution);
      // Perpendicular vector for thickness
      vec2 normal = vec2(-dir.y, dir.x);

      // Offset by half thickness in pixels, converted to NDC space.
      // Henrik: treat u_line_width as the final thickness in pixels.
      float half_width = (u_line_width * a_width) * 0.5;
      // Trim caps slightly so adjacent segments do not overdraw at joins.
      vec2 offset =
        (normal * (half_width / u_resolution) * a_side) +
        (dir * (half_width / u_resolution) * (-a_along));
      // Henrik: avoid extending segment caps; overdraw at joins otherwise makes corners look thicker.
      // Write clip-space position so Mapbox depth/clip behaves correctly (including below-ground).
      gl_Position = vec4((screen_pos + offset) * clip_pos.w, clip_pos.z, clip_pos.w);
    }
  `;

  const fsSource = `
    precision highp float;
    uniform vec2 u_dash_params;
    uniform float u_pixels_per_mercator;
    varying vec4 v_color;
    varying float v_distance;
    varying float v_is_dashed;
    void main() {
      if (v_is_dashed > 0.5) {
        float pixel_dist = v_distance * u_pixels_per_mercator;
        float total = u_dash_params.x + u_dash_params.y;
        if (mod(pixel_dist, total) > u_dash_params.x) {
          discard;
        }
      }
      gl_FragColor = v_color;
    }
  `;

  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const program = linkProgram(gl, vs, fs);

  const buffer = gl.createBuffer();
  const overlayBuffer = gl.createBuffer();
  if (!buffer || !overlayBuffer) {
    gl.deleteProgram(program);
    if (buffer) gl.deleteBuffer(buffer);
    if (overlayBuffer) gl.deleteBuffer(overlayBuffer);
    throw new Error('Failed to create wireframe WebGL buffer');
  }

  const aPositionHigh = gl.getAttribLocation(program, 'a_pos_high');
  const aPositionLow = gl.getAttribLocation(program, 'a_pos_low');
  const aOtherHigh = gl.getAttribLocation(program, 'a_other_high');
  const aOtherLow = gl.getAttribLocation(program, 'a_other_low');
  const aSide = gl.getAttribLocation(program, 'a_side');
  const aAlong = gl.getAttribLocation(program, 'a_along');
  const aColor = gl.getAttribLocation(program, 'a_color');
  const aData = gl.getAttribLocation(program, 'a_data');
  const aWidth = gl.getAttribLocation(program, 'a_width');

  const uMatrix = gl.getUniformLocation(program, 'u_matrix');
  const uDashParams = gl.getUniformLocation(program, 'u_dash_params');
  const uPixelsPerMercator = gl.getUniformLocation(
    program,
    'u_pixels_per_mercator',
  );
  const uResolution = gl.getUniformLocation(program, 'u_resolution');
  const uLineWidth = gl.getUniformLocation(program, 'u_line_width');

  if (
    aPositionHigh < 0 ||
    aPositionLow < 0 ||
    aOtherHigh < 0 ||
    aOtherLow < 0 ||
    aSide < 0 ||
    aAlong < 0 ||
    aColor < 0 ||
    aData < 0 ||
    aWidth < 0 ||
    !uMatrix ||
    !uDashParams ||
    !uPixelsPerMercator ||
    !uResolution ||
    !uLineWidth
  ) {
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    throw new Error('Failed to resolve wireframe shader locations');
  }

  return {
    program,
    buffer,
    overlayBuffer,
    aPositionHigh,
    aPositionLow,
    aOtherHigh,
    aOtherLow,
    aSide,
    aAlong,
    aColor,
    aData,
    aWidth,
    uMatrix,
    uDashParams,
    uPixelsPerMercator,
    uResolution,
    uLineWidth,
  };
}
